import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { leadsApi } from '../../services/api';
import { getCached } from '../../services/lookupCache';
import toast from 'react-hot-toast';
import {
  HiOutlineArrowLeft,
  HiOutlineDocumentText,
  HiOutlinePencil
} from 'react-icons/hi';
import LeadForm from '../../components/Forms/LeadForm';
import {
  formatCalendarDateDMY,
  formatDateTimeShortIST,
  calculateDetailedAgeIST,
} from '../../utils/timezoneDisplay';

// Date-only fields (API returns YYYY-MM-DD or ISO): display in Asia/Kolkata wall calendar
const formatDateOnly = (dateString) => formatCalendarDateDMY(dateString);

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLog, setNewLog] = useState({ type: 'call', content: '', call_outcome: '' });
  const [addingLog, setAddingLog] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([]);

  useEffect(() => {
    fetchLead();
    fetchCommunications();
    fetchFiltersData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchFiltersData = async () => {
    try {
      const [sourcesRes, statusesRes] = await Promise.all([
        getCached('leads:sources', () => leadsApi.getSources()),
        getCached('leads:statuses', () => leadsApi.getStatuses()),
      ]);
      setSources(sourcesRes.data.data);
      setStatuses(statusesRes.data.data);
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  const fetchLead = async () => {
    try {
      const response = await leadsApi.getById(id);
      setLead(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch lead');
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchCommunications = async () => {
    try {
      const response = await leadsApi.getCommunications(id);
      setCommunications(response.data.data);
    } catch (error) {
      console.error('Error fetching communications:', error);
    }
  };

  const handleAddCommunication = async (e) => {
    e.preventDefault();
    setAddingLog(true);

    try {
      await leadsApi.addCommunication(id, newLog);
      toast.success('Communication logged');
      setNewLog({ type: 'call', content: '', call_outcome: '' });
      fetchCommunications();
    } catch (error) {
      toast.error('Failed to add communication');
    } finally {
      setAddingLog(false);
    }
  };

  const handleCreateAssessment = () => {
    // Navigate to assessments page with lead data to pre-fill assessment form
    navigate('/assessments', { 
      state: { 
        fromLead: true, 
        leadData: {
          lead_id: lead.id, // Store lead_id to link assessment to lead
          full_name: lead.full_name,
          dob: lead.dob,
          gender: lead.gender,
          address: lead.address,
          contact_primary: lead.contact_primary,
          contact_whatsapp: lead.contact_whatsapp,
          email: lead.email
        }
      } 
    });
  };

  const handleFormSuccess = () => {
    setShowEditForm(false);
    fetchLead(); // Refresh lead data after update
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!lead) return null;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/leads')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <HiOutlineArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-bold text-gray-900">{lead.full_name}</h1>
          <p className="text-gray-500">Lead Details</p>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setShowEditForm(true)} 
            className="btn btn-secondary"
          >
            <HiOutlinePencil className="w-5 h-5 mr-2" />
            Edit Lead
          </button>
          {lead.status_name !== 'Enrolled' && (
            <button onClick={handleCreateAssessment} className="btn btn-primary">
              <HiOutlineDocumentText className="w-5 h-5 mr-2" />
              Create Assessment
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lead info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">Lead Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Full Name</p>
                <p className="font-medium">{lead.full_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Guardian Name</p>
                <p className="font-medium">{lead.guardian_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Date of Birth</p>
                <p className="font-medium">
                  {lead.dob ? formatDateOnly(lead.dob) : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Age</p>
                <p className="font-medium">
                  {lead.dob ? calculateDetailedAgeIST(lead.dob) : (lead.calculated_age || lead.age ? `${lead.calculated_age || lead.age} years` : '-')}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Gender</p>
                <p className="font-medium capitalize">{lead.gender || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Inquiry Type</p>
                <p className="font-medium capitalize">{lead.inquiry_type}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Primary Contact</p>
                <p className="font-medium">
                  <a href={`tel:${lead.contact_primary}`} className="text-primary-600 hover:underline">
                    {lead.contact_primary}
                  </a>
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">WhatsApp</p>
                <p className="font-medium">{lead.contact_whatsapp || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">
                  {lead.email ? (
                    <a href={`mailto:${lead.email}`} className="text-primary-600 hover:underline">
                      {lead.email}
                    </a>
                  ) : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Source</p>
                <p className="font-medium">{lead.source_name || '-'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-gray-500">Address</p>
                <p className="font-medium">{lead.address || '-'}</p>
              </div>
            </div>
          </div>

          {/* Status & Remarks */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">Status & Remarks</h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">Current Status</p>
                <span 
                  className="badge mt-1"
                  style={{ 
                    backgroundColor: `${lead.status_color}20`, 
                    color: lead.status_color 
                  }}
                >
                  {lead.status_name}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-500">Remarks</p>
                <p className="font-medium whitespace-pre-wrap">{lead.remarks || 'No remarks'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Follow-up Count</p>
                <p className="font-medium">{lead.follow_up_count} attempts</p>
              </div>
              {lead.next_follow_up && (
                <div>
                  <p className="text-sm text-gray-500">Next Follow-up</p>
                  <p className="font-medium">{formatDateOnly(lead.next_follow_up)}</p>
                </div>
              )}
              {lead.next_appointment_date && (
                <div>
                  <p className="text-sm text-gray-500">Next Appointment</p>
                  <p className="font-medium">
                    {formatDateOnly(lead.next_appointment_date)}
                    {lead.next_appointment_time && (
                      <span className="ml-2 text-gray-600">
                        at {lead.next_appointment_time.substring(0, 5)}
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Communication log */}
        <div className="space-y-6">
          {/* Add communication */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">Log Communication</h3>
            <form onSubmit={handleAddCommunication} className="space-y-4">
              <div>
                <label className="label">Type</label>
                <select
                  value={newLog.type}
                  onChange={(e) => setNewLog({ ...newLog, type: e.target.value })}
                  className="input"
                >
                  <option value="call">Call</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="email">Email</option>
                  <option value="meeting">Meeting</option>
                </select>
              </div>
              {newLog.type === 'call' && (
                <div>
                  <label className="label">Outcome</label>
                  <select
                    value={newLog.call_outcome}
                    onChange={(e) => setNewLog({ ...newLog, call_outcome: e.target.value })}
                    className="input"
                  >
                    <option value="">Select outcome</option>
                    <option value="answered">Answered</option>
                    <option value="not_answered">Not Answered</option>
                    <option value="busy">Busy</option>
                    <option value="wrong_number">Wrong Number</option>
                  </select>
                </div>
              )}
              <div>
                <label className="label">Notes</label>
                <textarea
                  value={newLog.content}
                  onChange={(e) => setNewLog({ ...newLog, content: e.target.value })}
                  className="input"
                  rows={3}
                  placeholder="Enter notes..."
                />
              </div>
              <button 
                type="submit" 
                className="btn btn-primary w-full"
                disabled={addingLog}
              >
                {addingLog ? 'Saving...' : 'Log Communication'}
              </button>
            </form>
          </div>

          {/* Communication history */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">Communication History</h3>
            {communications.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No communications yet</p>
            ) : (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {communications.map((comm) => (
                  <div key={comm.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="badge bg-gray-200 text-gray-700 capitalize">
                        {comm.type}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatDateTimeShortIST(comm.created_at)}
                      </span>
                    </div>
                    {comm.call_outcome && (
                      <p className="text-sm text-gray-600 mb-1">
                        Outcome: <span className="capitalize">{comm.call_outcome}</span>
                      </p>
                    )}
                    <p className="text-sm">{comm.content || 'No notes'}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      By: {comm.logged_by_name}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Lead Form Modal */}
      {showEditForm && lead && (
        <LeadForm
          lead={lead}
          sources={sources}
          statuses={statuses}
          onClose={() => setShowEditForm(false)}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default LeadDetail;

